import React from 'react';
import UIcon from '@/assets/uicons/UIcon';
import styles from './Avatar.module.scss';

export interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'circle' | 'square';
  color?: string;
  icon?: string;
  className?: string;
  onClick?: () => void;
  status?: 'online' | 'offline' | 'away' | 'busy';
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = '',
  name = '',
  size = 'md',
  shape = 'circle',
  color,
  icon,
  className = '',
  onClick,
  status
}) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRandomColor = (name: string) => {
    const colors = [
      '#A9DCD9', // Aqua
      '#FFC7D8', // Pink
      '#F8DF72', // Yellow
      '#B5B8F8', // Light Indigo
      '#B6E3A8', // Light Green
      '#FFD6A5', // Peach
      '#C3B6F8', // Lilac
      '#F9AAEF', // Magenta
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % colors.length;
    return colors[idx];
  };

  const avatarColor = color || getRandomColor(name);
  const initials = getInitials(name);

  const avatarClasses = [
    styles.avatar,
    styles[`size${size.charAt(0).toUpperCase() + size.slice(1)}`],
    styles[`shape${shape.charAt(0).toUpperCase() + shape.slice(1)}`],
    onClick ? styles.clickable : '',
    className
  ].filter(Boolean).join(' ');

  const statusClasses = status ? [
    styles.status,
    styles[`status${status.charAt(0).toUpperCase() + status.slice(1)}`]
  ].join(' ') : '';

  const renderContent = () => {
    if (src) {
      return (
        <img
          src={src}
          alt={alt}
          className={styles.avatarImage}
          onError={(e) => {
            // Fallback a iniciales si la imagen falla
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.classList.add(styles.fallback);
            }
          }}
        />
      );
    }

    if (icon) {
      return (
        <UIcon
          name={icon}
          size={getIconSize(size)}
          className={styles.avatarIcon}
        />
      );
    }

    if (name) {
      return (
        <span className={styles.avatarText}>
          {initials}
        </span>
      );
    }

    return (
      <UIcon
        name="users"
        size={getIconSize(size)}
        className={styles.avatarIcon}
      />
    );
  };

  const getIconSize = (size: string) => {
    const sizeMap: Record<string, number> = {
      xs: 12,
      sm: 16,
      md: 20,
      lg: 24,
      xl: 32
    };
    return sizeMap[size] || 20;
  };

  return (
    <div
      className={avatarClasses}
      style={{ backgroundColor: avatarColor }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      title={name || alt}
    >
      {renderContent()}
      {status && (
        <div className={statusClasses} />
      )}
    </div>
  );
};

export default Avatar; 